/**
 * Copy for the passkey account screen (SPEC §9.3, USR-11).
 *
 * **The device question carries the whole of the shared-workstation control's
 * usability**, so its wording is load-bearing rather than decorative. Two
 * things it deliberately does not do:
 *
 * - **It does not offer "not sure".** The question has an operationally correct
 *   answer, and a guess is precisely the failure mode — somebody guessing
 *   "mine" at a squad-room machine creates a credential several people can
 *   present, which is worse than the emailed code it replaced.
 * - **It does not say "platform authenticator" or "cross-platform".** The
 *   person answering is a deputy at a shift change, and the honest translation
 *   of the constraint is *a computer other people use* and *something you take
 *   with you*.
 *
 * Voice per `design/design-system/readme.md`: sentence case, no emoji, plain
 * words. The one place a security term survives is "passkey" itself, which is
 * what every browser's own dialog will call it.
 */
export const PASSKEYS_COPY = {
  title: 'Secure sign-in',
  intro:
    'A passkey signs you in with your fingerprint, face, or a security key. There is no code to wait for and nothing to type.',

  list: {
    heading: 'Your passkeys',
    empty: 'You have no passkeys yet. Add one to sign in without a code.',
    added: 'Added',
    lastUsed: 'Last used',
    neverUsed: 'Not used yet',
    synced: 'Synced to your password manager',
    sharedDevice: 'For a shared computer',
    rename: 'Rename',
    remove: 'Remove',
    /** Rendered when a rename is open. The label is the only editable field. */
    nameLabel: 'Passkey name',
    save: 'Save',
    cancel: 'Cancel',
  },

  add: {
    action: 'Add a passkey',
    /**
     * The prompt after the first one lands. USR-11a rung 0, and the highest
     * leverage sentence on the screen: two credentials makes recovery
     * self-service, and every rung below it needs somebody else.
     */
    second:
      'Add a second passkey. If you lose this one, a spare is the difference between signing in and calling your administrator.',
    dismissSecond: 'Not now',
  },

  question: {
    heading: 'Whose computer is this?',
    /** Why the answer matters, in the terms the answer is actually about. */
    help: 'This decides what kind of passkey you can create. It cannot be changed afterwards — remove the passkey and add another instead.',
    own: 'Mine, or one assigned to me',
    ownHelp: 'You can use this device’s own fingerprint, face, or PIN.',
    shared: 'A computer other people also use',
    sharedHelp:
      'On a shared computer your passkey has to be something you take with you — a security key, or your phone.',
    continue: 'Continue',
    cancel: 'Cancel',
  },

  naming: {
    heading: 'Name this passkey',
    help: 'Something you will recognise later, like “Work laptop” or “Yubikey on my keys”.',
    placeholder: 'Work laptop',
    create: 'Create passkey',
    cancel: 'Cancel',
  },

  removing: {
    heading: 'Remove this passkey?',
    /**
     * The consequence stated plainly, because it is one people do not expect:
     * removing a credential signs every browser out. That is deliberate — the
     * act somebody performs when a laptop is stolen is *remove the passkey*.
     */
    warning: 'You will be signed out everywhere, on every device.',
    lastOne: 'This is your only passkey. Without it you will sign in with an emailed code again.',
    confirm: 'Remove passkey',
    cancel: 'Keep it',
  },

  errors: {
    load: 'Your passkeys could not be loaded.',
    /** A cancelled browser dialog. Not a failure, and not phrased as one. */
    cancelled: 'No passkey was created.',
    create: 'That passkey could not be created.',
    rename: 'That passkey could not be renamed.',
    remove: 'That passkey could not be removed.',
    /** The environment has no relying party configured — a 503 from the API. */
    unavailable: 'Secure sign-in is not available yet.',
  },

  /*
    **The `restricted` block is gone** (ADR-0051). It carried a second heading
    and body for this screen, plus "Use the passkey you already have" and the
    control under it, because a restricted session (SYS-10) was redirected here
    on arrival and this was where USR-11a's rung 1 was meant to finish. Both
    moved to the sign-in landing page: the restriction is about which credential
    opened the session, so the screen that decides that is where it belongs — and
    that screen already has the `canHoldSession` guard whose absence here made
    the control inert in every deployed build.

    Nothing on this screen is conditional on the session's assurance any more.
  */
} as const;
